import { type ValidationMode } from '@wix/ambassador-forms-v1-validation-result/types';
import {
  type FieldsInputField,
  InputFieldInputType,
  ValidationFormat,
} from '@wix/ambassador-forms-v4-form/types';
import { type SomeJSONSchema } from 'ajv/dist/types/json-schema';

import { asRequired } from '../../../utils/lang';
import { mapToBooleanSchemaProperty } from '../boolean-schema-property-mapper';
import { mapToNumberSchemaProperty } from '../number-schema-property-mapper';
import { mapToObjectSchemaProperty } from '../object-schema-property-mapper';
import { mapToPredefinedSchemaProperty } from '../predefined-schema-property-mapper';

import { arraySchemaPropertyMapperPlatformised } from './array-schema-property-mapper-platformised';
import { mapStringInputFieldToSchema } from './string-input-field-schema-mapper';

export type PlatformisedFieldToValidationTransformer =
  typeof transformPlatformisedFieldToValidation;

export function transformPlatformisedFieldToValidation(
  inputField: FieldsInputField,
  validationMode: ValidationMode,
  libraryBasedPhoneFieldValidationEnabled: boolean = false,
): Omit<SomeJSONSchema, '$id'> | undefined {
  switch (inputField.inputType) {
    case InputFieldInputType.STRING:
      return mapStringInputFieldToSchema(
        inputField,
        validationMode,
        libraryBasedPhoneFieldValidationEnabled,
      );
    case InputFieldInputType.ARRAY:
      return arraySchemaPropertyMapperPlatformised(
        inputField,
        validationMode,
        libraryBasedPhoneFieldValidationEnabled,
      );
    case InputFieldInputType.NUMBER:
      const numberValidation = asRequired(
        inputField.numberOptions?.validation,
        inputField,
        'numberOptions.validation',
      );
      return mapToNumberSchemaProperty(numberValidation, validationMode);
    case InputFieldInputType.BOOLEAN:
      const booleanValidation = asRequired(
        inputField.booleanOptions?.validation,
        inputField,
        'booleanOptions.validation',
      );
      return mapToBooleanSchemaProperty(booleanValidation, validationMode);
    case InputFieldInputType.OBJECT:
      const objectValidation = asRequired(
        inputField.objectOptions?.validation,
        inputField,
        'objectOptions.validation',
      );
      return mapToObjectSchemaProperty(objectValidation, validationMode);
    case InputFieldInputType.WIX_FILE:
      return mapToPredefinedSchemaProperty(
        inputField.target!,
        {
          format: ValidationFormat.WIX_FILE,
        },
        validationMode,
      );
    case InputFieldInputType.SCHEDULING:
      return mapToPredefinedSchemaProperty(
        inputField.target!,
        {
          format: ValidationFormat.SCHEDULING,
        },
        validationMode,
      );
    case InputFieldInputType.PAYMENT:
      return mapToPredefinedSchemaProperty(
        inputField.target!,
        {
          paymentOptions: inputField.paymentOptions?.validation,
          format: ValidationFormat.PAYMENT,
        },
        validationMode,
      );
    default:
      return undefined;
  }
}
